/**
 * GraphInterpreter — adapts the frozen Redprint {@link GraphExecutor} to the unified
 * {@link IScriptExecutor} contract, so a `.rp` graph and a TypeScript {@link Behaviour}
 * run through the SAME lifecycle + the SAME {@link IEngineContext}.
 *
 * Lives in the player package because it bridges core (`IScriptExecutor`) and redprint
 * (`GraphExecutor`); core depends on redprint, so this adapter cannot live in either
 * without a cycle.
 *
 * Lifecycle mapping note: `GraphExecutor.initialize()` bundles the graph's awake → enable →
 * start AND resets variables to graph defaults. So this adapter runs it inside `onAwake()`
 * (after which `onEnable`/`onStart` are no-ops) and applies inspector variable overrides
 * *after* that reset, otherwise they would be clobbered.
 */

import type { EntityHandle, IEngineContext, IScriptExecutor, ScriptDefinition } from "@engine/core";
import { NO_ENTITY } from "@engine/core";
import { GraphExecutor, type Graph, type NodeRegistry, type PortValue } from "@engine/redprint";
import { contextToBridge } from "./contextToBridge";

export class GraphInterpreter implements IScriptExecutor {
  private _executor: GraphExecutor | null = null;
  private _entity: EntityHandle = NO_ENTITY;
  private _pendingVars = new Map<string, unknown>();
  private _started = false;

  constructor(
    private readonly _graph: Graph,
    private readonly _registry: NodeRegistry,
  ) {}

  initialize(entity: EntityHandle, ctx: IEngineContext): void {
    this._entity = entity;
    this._executor = new GraphExecutor(this._graph, this._registry, contextToBridge(ctx));
  }

  onAwake(): void {
    if (!this._executor || this._started) return;
    // Fires the graph's awake/enable/start and resets variables to defaults…
    this._executor.initialize(this._entity);
    // …so apply inspector overrides afterwards.
    for (const [name, value] of this._pendingVars) {
      this._executor.setVariable(name, value as PortValue);
    }
    this._pendingVars.clear();
    this._started = true;
  }

  // The graph fired enable/start inside initialize(); these stay no-ops.
  onEnable(): void {}
  onStart(): void {}

  onUpdate(dt: number): void {
    this._executor?.update(dt);
  }
  onFixedUpdate(dt: number): void {
    this._executor?.fixedUpdate(dt);
  }
  onLateUpdate(dt: number): void {
    this._executor?.lateUpdate(dt);
  }
  onDisable(): void {
    this._executor?.disable();
  }
  onDestroy(): void {
    this._executor?.destroy();
  }

  getVariable<T = unknown>(name: string): T {
    if (this._executor && this._started) return this._executor.getVariable<T>(name);
    return this._pendingVars.get(name) as T;
  }

  setVariable(name: string, value: unknown): void {
    if (this._executor && this._started) {
      this._executor.setVariable(name, value as PortValue);
    } else {
      this._pendingVars.set(name, value);
    }
  }

  getVariableSnapshot(): Record<string, unknown> {
    if (this._executor && this._started) return this._executor.getVariableSnapshot();
    return Object.fromEntries(this._pendingVars);
  }

  triggerEvent(eventId: string, params?: Record<string, unknown>): void {
    this._executor?.triggerEvent(eventId, params as Record<string, PortValue> | undefined);
  }

  getDefinition(): ScriptDefinition {
    return {
      name: "RedprintGraph",
      fields: this._graph.getVariables().map((v) => ({ name: v.name, type: String(v.type) })),
      methods: [],
      executeOrder: 0,
    };
  }
}
